import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from '../../shared/components/Icons';
import { aggregateStudyUnit } from '../../shared/utils/studyUnitAggregation';
import { formatCovers, cleanSubtopicName } from '../../shared/utils/topicDisplay';
import { bandHex, bandKey, BAND_LABEL, BADGE_BG, BADGE_TEXT } from './bandColors';
import TopicDetailPanel from './TopicDetailPanel';

// ── Ring with % label (canonical v2: 44×44 / r=18 / stroke 3.5) ──
function ConfidenceRing({ value }) {
  const CIRC = 2 * Math.PI * 18; // ≈ 113.1
  if (value === null || value === undefined) {
    return (
      <div className="su-ring">
        <svg viewBox="0 0 44 44">
          <circle className="ring-bg" cx="22" cy="22" r="18" />
        </svg>
        <span className="ring-pct" style={{ color: 'var(--text-muted)' }}>—</span>
      </div>
    );
  }
  const pct = Math.max(0, Math.min(1, value));
  const offset = CIRC * (1 - pct);
  const stroke = bandHex(value);
  return (
    <div className="su-ring">
      <svg viewBox="0 0 44 44">
        <circle className="ring-bg" cx="22" cy="22" r="18" />
        <circle
          className="ring-fill"
          cx="22" cy="22" r="18"
          style={{ stroke }}
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="ring-pct">{Math.round(pct * 100)}%</span>
    </div>
  );
}

// ── Compact L3 row inside an expanded study unit ──────────────────────
// Clicking toggles the detail panel directly below (selected state).
// The panel visually connects flush via margin-top: -1px + shared blue
// border — see .l3-row.selected + .detail-panel in styles.css.
function L3Row({ topic, selected, onClick, statuses, onSave, onNavigateToSRA }) {
  const displayName = cleanSubtopicName(topic.display_name || topic.subtopic || '');
  const conf = topic.raw_confidence ?? topic.confidence;
  const band = bandKey(conf);
  const pctText = conf !== null && conf !== undefined ? `${Math.round(conf * 100)}%` : '—';
  const dotClass = band === 'weak' ? 'd-r' : band === 'needs-work' ? 'd-a' : band === 'strong' ? 'd-g' : 'd-n';
  const pctClass = band === 'weak' ? 'c-r' : band === 'needs-work' ? 'c-a' : band === 'strong' ? 'c-g' : 'c-n';
  return (
    <>
      <div
        className={`l3-row${selected ? ' l3-row-selected' : ''}`}
        onClick={onClick}
        role="button"
        aria-expanded={selected}
      >
        <span className={`l3-dot ${dotClass}`} />
        <span className="l3-name">{displayName}</span>
        <span className={`l3-pct ${pctClass}`}>{pctText}</span>
        <span className="l3-arrow" style={selected ? { transform: 'rotate(90deg)' } : undefined}>
          {selected ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </div>
      {selected && (
        <div className="l3-drill">
          <TopicDetailPanel
            topic={topic}
            onSave={onSave}
            statuses={statuses}
            onNavigateToSRA={onNavigateToSRA}
          />
        </div>
      )}
    </>
  );
}

// ── Main study unit card (canonical v2) ───────────────────────────────
// Collapsed: ring + title/sub + badge + qs count + chevron.
// Expanded: border-top separator, covers row, DERIVED score text
// (no slider — study unit rating is computed from L3 members), L3 list.
function StudyUnitRow({ studyUnit, members, onSave, moduleColor, statuses, focusTopicId, onNavigateToSRA }) {
  const [open, setOpen] = useState(false);
  const [selectedL3Id, setSelectedL3Id] = useState(null);

  const agg = aggregateStudyUnit(members);
  const band = bandKey(agg.confidence);
  const weakCount = members.filter(m => bandKey(m.raw_confidence ?? m.confidence) === 'weak').length;

  const subtopicWord = agg.totalCount === 1 ? 'subtopic' : 'subtopics';
  const subtitle = weakCount > 0
    ? `${agg.totalCount} ${subtopicWord} · ${weakCount} weak area${weakCount === 1 ? '' : 's'}`
    : `${agg.totalCount} ${subtopicWord}`;

  const derivedText = agg.confidence !== null
    ? `Avg: ${Math.round(agg.confidence * 100)}%`
    : '— no ratings yet';

  const questionsAvailable = studyUnit.questions_available ?? 0;

  return (
    <div className={`su-card${open ? ' expanded' : ''}`}>
      <div className="su-main" onClick={() => setOpen(o => !o)}>
        <ConfidenceRing value={agg.confidence} />
        <div className="su-info">
          <div className="su-title">{studyUnit.name}</div>
          <div className="su-sub">{subtitle}</div>
        </div>
        <div className="su-right">
          <span
            className="su-badge"
            style={{ background: BADGE_BG[band], color: BADGE_TEXT[band] }}
          >
            {BAND_LABEL[band]}
          </span>
          <span className="su-qs">{questionsAvailable} Qs</span>
          <span className={`su-chevron${open ? ' open' : ''}`}>
            {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </span>
        </div>
      </div>

      {open && (
        <div className="su-expanded" onClick={e => e.stopPropagation()}>
          {studyUnit.covers_items && (
            <div className="su-covers-row">
              <span className="su-covers-label">Covers</span>
              <span className="su-covers-text">{formatCovers(studyUnit.covers_items)}</span>
            </div>
          )}
          <div className="su-derived-row">
            <span className="su-derived-label">
              Tap a subtopic below to rate it — study unit score is derived from subtopics.
            </span>
            <span className="su-derived-val">{derivedText}</span>
          </div>
          <div className="l3-list">
            {members.map(m => (
              <L3Row
                key={m.id}
                topic={m}
                selected={selectedL3Id === m.id}
                onClick={() => setSelectedL3Id(id => id === m.id ? null : m.id)}
                statuses={statuses}
                onSave={onSave}
                onNavigateToSRA={onNavigateToSRA}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudyUnitRow;
